package com.alpha.showcase.common.cache.dao

import androidx.room3.EntityInsertAdapter
import androidx.room3.RoomDatabase
import androidx.room3.util.getColumnIndexOrThrow
import androidx.room3.util.performSuspending
import androidx.sqlite.SQLiteStatement
import com.alpha.showcase.common.cache.entity.CacheMetadata
import kotlin.Boolean
import kotlin.Int
import kotlin.Long
import kotlin.String
import kotlin.Suppress
import kotlin.Unit
import kotlin.collections.List
import kotlin.reflect.KClass

@Suppress(names = ["UNCHECKED_CAST", "DEPRECATION", "REDUNDANT_PROJECTION", "REMOVAL", "MemberExtensionConflict"])
internal class CacheMetadataDao_Impl(
  __db: RoomDatabase,
) : CacheMetadataDao {
  private val __db: RoomDatabase

  private val __insertAdapterOfCacheMetadata: EntityInsertAdapter<CacheMetadata>
  init {
    this.__db = __db
    this.__insertAdapterOfCacheMetadata = object : EntityInsertAdapter<CacheMetadata>() {
      protected override fun createQuery(): String = "INSERT OR REPLACE INTO `cache_metadata` (`id`,`source_type`,`source_key`,`last_updated`,`next_update_time`,`total_items`,`update_strategy`,`is_recursive`,`version`,`status`,`source_config_hash`,`committed_sync_version`) VALUES (nullif(?, 0),?,?,?,?,?,?,?,?,?,?,?)"

      protected override fun bind(statement: SQLiteStatement, entity: CacheMetadata) {
        statement.bindLong(1, entity.id)
        statement.bindText(2, entity.sourceType)
        statement.bindText(3, entity.sourceKey)
        statement.bindLong(4, entity.lastUpdated)
        statement.bindLong(5, entity.nextUpdateTime)
        statement.bindLong(6, entity.totalItems.toLong())
        statement.bindText(7, entity.updateStrategy)
        val _tmp: Int = if (entity.isRecursive) 1 else 0
        statement.bindLong(8, _tmp.toLong())
        val _tmpVersion: String? = entity.version
        if (_tmpVersion == null) {
          statement.bindNull(9)
        } else {
          statement.bindText(9, _tmpVersion)
        }
        statement.bindText(10, entity.status)
        val _tmpSourceConfigHash: String? = entity.sourceConfigHash
        if (_tmpSourceConfigHash == null) {
          statement.bindNull(11)
        } else {
          statement.bindText(11, _tmpSourceConfigHash)
        }
        statement.bindLong(12, entity.committedSyncVersion)
      }
    }
  }

  public override suspend fun insertOrReplace(metadata: CacheMetadata): Unit = performSuspending(__db, false, true) { _connection ->
    __insertAdapterOfCacheMetadata.insert(_connection, metadata)
  }

  public override suspend fun getBySource(sourceType: String, sourceKey: String): CacheMetadata? {
    val _sql: String = """
        |
        |        SELECT * FROM cache_metadata
        |        WHERE source_type = ? AND source_key = ?
        |        LIMIT 1
        |        
        """.trimMargin()
    return performSuspending(__db, true, false) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindText(_argIndex, sourceType)
        _argIndex = 2
        _stmt.bindText(_argIndex, sourceKey)
        val _columnIndexOfId: Int = getColumnIndexOrThrow(_stmt, "id")
        val _columnIndexOfSourceType: Int = getColumnIndexOrThrow(_stmt, "source_type")
        val _columnIndexOfSourceKey: Int = getColumnIndexOrThrow(_stmt, "source_key")
        val _columnIndexOfLastUpdated: Int = getColumnIndexOrThrow(_stmt, "last_updated")
        val _columnIndexOfNextUpdateTime: Int = getColumnIndexOrThrow(_stmt, "next_update_time")
        val _columnIndexOfTotalItems: Int = getColumnIndexOrThrow(_stmt, "total_items")
        val _columnIndexOfUpdateStrategy: Int = getColumnIndexOrThrow(_stmt, "update_strategy")
        val _columnIndexOfIsRecursive: Int = getColumnIndexOrThrow(_stmt, "is_recursive")
        val _columnIndexOfVersion: Int = getColumnIndexOrThrow(_stmt, "version")
        val _columnIndexOfStatus: Int = getColumnIndexOrThrow(_stmt, "status")
        val _columnIndexOfSourceConfigHash: Int = getColumnIndexOrThrow(_stmt, "source_config_hash")
        val _columnIndexOfCommittedSyncVersion: Int = getColumnIndexOrThrow(_stmt, "committed_sync_version")
        val _result: CacheMetadata?
        if (_stmt.step()) {
          val _tmpId: Long
          _tmpId = _stmt.getLong(_columnIndexOfId)
          val _tmpSourceType: String
          _tmpSourceType = _stmt.getText(_columnIndexOfSourceType)
          val _tmpSourceKey: String
          _tmpSourceKey = _stmt.getText(_columnIndexOfSourceKey)
          val _tmpLastUpdated: Long
          _tmpLastUpdated = _stmt.getLong(_columnIndexOfLastUpdated)
          val _tmpNextUpdateTime: Long
          _tmpNextUpdateTime = _stmt.getLong(_columnIndexOfNextUpdateTime)
          val _tmpTotalItems: Int
          _tmpTotalItems = _stmt.getLong(_columnIndexOfTotalItems).toInt()
          val _tmpUpdateStrategy: String
          _tmpUpdateStrategy = _stmt.getText(_columnIndexOfUpdateStrategy)
          val _tmpIsRecursive: Boolean
          val _tmp: Int
          _tmp = _stmt.getLong(_columnIndexOfIsRecursive).toInt()
          _tmpIsRecursive = _tmp != 0
          val _tmpVersion: String?
          if (_stmt.isNull(_columnIndexOfVersion)) {
            _tmpVersion = null
          } else {
            _tmpVersion = _stmt.getText(_columnIndexOfVersion)
          }
          val _tmpStatus: String
          _tmpStatus = _stmt.getText(_columnIndexOfStatus)
          val _tmpSourceConfigHash: String?
          if (_stmt.isNull(_columnIndexOfSourceConfigHash)) {
            _tmpSourceConfigHash = null
          } else {
            _tmpSourceConfigHash = _stmt.getText(_columnIndexOfSourceConfigHash)
          }
          val _tmpCommittedSyncVersion: Long
          _tmpCommittedSyncVersion = _stmt.getLong(_columnIndexOfCommittedSyncVersion)
          _result = CacheMetadata(_tmpId,_tmpSourceType,_tmpSourceKey,_tmpLastUpdated,_tmpNextUpdateTime,_tmpTotalItems,_tmpUpdateStrategy,_tmpIsRecursive,_tmpVersion,_tmpStatus,_tmpSourceConfigHash,_tmpCommittedSyncVersion)
        } else {
          _result = null
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun deleteBySource(sourceType: String, sourceKey: String) {
    val _sql: String = "DELETE FROM cache_metadata WHERE source_type = ? AND source_key = ?"
    return performSuspending(__db, false, true) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindText(_argIndex, sourceType)
        _argIndex = 2
        _stmt.bindText(_argIndex, sourceKey)
        _stmt.step()
      } finally {
        _stmt.close()
      }
    }
  }

  public companion object {
    public fun getRequiredColumnConverters(): List<KClass<*>> = emptyList()

    public fun getRequiredDaoReturnTypeConverters(): List<KClass<*>> = emptyList()
  }
}
